"use client"

import { useState, useEffect, useRef } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import axios from "axios"
import loginVector from "../Assets/login-vector.png"
import logo from "../Assets/logo.png"

function VerifyEmail() {
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState(location.state?.username || "")
  const [otp, setOtp] = useState("")
  const [otpArr, setOtpArr] = useState(["", "", "", "", "", ""])
  const [step, setStep] = useState("email") // "email" | "otp"
  const [errorMessage, setErrorMessage] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    localStorage.removeItem("token")
  }, [])

  // Optional: jika sudah login, redirect ke dashboard
  useEffect(() => {
    const token = localStorage.getItem("token")
    if (token) {
      navigate("/dashboard")
    }
  }, [navigate])

  // Sinkronkan otpArr ke otp string
  useEffect(() => {
    setOtp(otpArr.join(""))
  }, [otpArr])

  const handleSendOtp = async (e) => {
    e.preventDefault()
    setErrorMessage("")
    setSuccessMessage("")
    setIsLoading(true)
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/users/resend-verification`, {
        email,
      })
      setSuccessMessage("OTP code has been sent to your email.")
      setStep("otp")
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message ||
        "Failed to send OTP. Please make sure your email is correct and try again."
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyOtp = async (e) => {
    e.preventDefault()
    setErrorMessage("")
    setSuccessMessage("")
    setIsLoading(true)
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/users/verify-email`, {
        email,
        verification_code: otp,
      })
      setSuccessMessage("Your email has been verified. Please login.")
      setTimeout(() => {
        navigate("/login")
      }, 2000)
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message ||
        "Invalid or expired OTP code."
      )
    } finally {
      setIsLoading(false)
    }
  }

  // Handler untuk input per kotak
  const handleOtpChange = (e, idx) => {
    const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 1)
    let newArr = [...otpArr]
    newArr[idx] = val
    setOtpArr(newArr)
    // Fokus ke kotak berikutnya jika ada input
    if (val && idx < 5) {
      document.getElementById(`verify-otp-input-${idx + 1}`)?.focus()
    }
  }

  const handleOtpKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !otpArr[idx] && idx > 0) {
      document.getElementById(`verify-otp-input-${idx - 1}`)?.focus()
    }
  }

  const handleOtpPaste = (e) => {
    const paste = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, 6)
    if (paste.length === 6) {
      setOtpArr(paste.split(""))
      setOtp(paste)
      setTimeout(() => {
        document.getElementById(`verify-otp-input-5`)?.focus()
      }, 0)
      e.preventDefault()
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-600 to-purple-600"></div>
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='https://www.w3.org/2000/svg'%3E%3Cg fill='none' fillRule='evenodd'%3E%3Cg fill='%234F46E5' fillOpacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute top-40 right-20 w-16 h-16 bg-purple-200 rounded-full opacity-20 animate-pulse delay-1000"></div>

      <div className="relative z-10 min-h-screen flex">
        {/* Left Side - Illustration & Branding */}
        <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 flex-col justify-center items-center p-12 relative">
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-purple-600/10 rounded-r-3xl"></div>

          {/* Content */}
          <div className="relative z-10 text-center max-w-lg">
            {/* Logo */}
            <div>
              <img src={logo || "/placeholder.svg"} alt="MagnaSight Logo" className="h-64 mx-auto" />
            </div>

            {/* Illustration */}
            <div className="mb-8">
              <img
                src={loginVector || "/placeholder.svg"}
                alt="Login Illustration"
                className="w-4/5 h-auto object-contain mx-auto drop-shadow-2xl"
              />
            </div>
          </div>
        </div>

        {/* Right Side - Verify Email Form */}
        <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-4 sm:p-6 lg:p-12 min-h-screen py-8 sm:py-12">
          <div className="w-full max-w-md my-auto">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center -mt-3 -mb-3 sm:mb-2">
              <img
                src={logo || "/placeholder.svg"}
                alt="MagnaSight Logo"
                className="w-auto mx-auto object-contain"
                style={{ height: "135px" }}
              />
            </div>

            {/* Verify Email Card */}
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-5 sm:p-8 lg:p-10">
              {/* Header */}
              <div className="text-center mb-5 sm:mb-8">
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 mb-1.5 sm:mb-2">Verify Email</h2>
                <p className="text-xs sm:text-sm text-gray-600">
                  {step === "email"
                    ? "OTP verification code will be sent to your email address."
                    : "Enter the OTP code sent to your email."}
                </p>
              </div>

              {/* Error Message */}
              {errorMessage && (
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-center">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 bg-red-100 rounded-full flex items-center justify-center mr-2.5 sm:mr-3 shrink-0">
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-red-500 rounded-full"></div>
                    </div>
                    <p className="text-red-700 text-xs sm:text-sm font-medium">{errorMessage}</p>
                  </div>
                </div>
              )}
              {/* Success Message */}
              {successMessage && (
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 bg-green-100 rounded-full flex items-center justify-center mr-2.5 sm:mr-3 shrink-0">
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full"></div>
                    </div>
                    <p className="text-green-700 text-xs sm:text-sm font-medium">{successMessage}</p>
                  </div>
                </div>
              )}

              {step === "email" ? (
                <form onSubmit={handleSendOtp} className="space-y-4 sm:space-y-6">
                  <div>
                    <label className="block text-gray-700 text-xs sm:text-sm font-semibold mb-1.5 sm:mb-2" htmlFor="email">
                      Email
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        id="email"
                        value={email}
                        readOnly
                        className="w-full px-4 py-2.5 sm:py-3 text-sm border border-gray-200 rounded-xl bg-gray-100 text-gray-500 cursor-not-allowed focus:outline-none"
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2.5 sm:py-3 px-6 rounded-xl text-sm sm:text-base font-semibold hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isLoading ? "Sending OTP..." : "Send OTP"}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp} className="space-y-6">
                  <div>
                    <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="otp">
                      OTP Code
                    </label>
                    <div className="flex gap-2 justify-start">
                      {[0,1,2,3,4,5].map((idx) => (
                        <input
                          key={idx}
                          id={`verify-otp-input-${idx}`}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          className="w-10 h-12 text-center text-xl border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50/50 hover:bg-white"
                          value={otpArr[idx]}
                          onChange={e => handleOtpChange(e, idx)}
                          onKeyDown={e => handleOtpKeyDown(e, idx)}
                          onPaste={e => handleOtpPaste(e)}
                          autoFocus={idx === 0}
                        />
                      ))}
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isLoading ? "Verifying..." : "Verify"}
                  </button>
                  {/* Hide change email button since email is not editable */}
                </form>
              )}
            </div>

            {/* Footer */}
            <div className="text-center text-gray-500 text-sm mt-8">
              <p>© Smartnet Magna Global {new Date().getFullYear()}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VerifyEmail
